//useStorage.js
import { storage } from "@/firebase/init";
import { db } from "@/firebase/init";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { doc, updateDoc, getDoc, setDoc, collection, getDocs, deleteDoc } from "firebase/firestore";
import Compressor from 'compressorjs';
import {usersCollection, profilePicturespath} from './useGlobalVariables'


/**
 * Uploads a compressed and resized profile picture, replaces the old one, and updates Firestore
 * @param {string} uid - User ID
 * @param {File} file - The image file to upload
 */
export const uploadProfilePicture = async (uid, file) => {
    try {
      if (!uid || !file) throw new Error("Invalid user ID or file.");
  
      // Compress and resize the image before uploading
      const resizedFile = await new Promise((resolve, reject) => {
        new Compressor(file, {
          maxWidth: 800,      // Max width of the image
          maxHeight: 800,     // Max height of the image
          quality: 0.8,       // Image quality (0 to 1)
          success: resolve,
          error: reject,
        });
      });
  
      // Define the fixed file name for every user (you can change the file extension if needed)
      const imageRef = ref(storage, `${profilePicturespath}/${uid}/profile.png`);
  
      // Upload resized and compressed file to Firebase Storage (overwrites existing one)
      await uploadBytes(imageRef, resizedFile);
  
      // Get the new download URL
      const downloadURL = await getDownloadURL(imageRef);
  
      // Update Firestore with the new profile picture URL
      await updateDoc(doc(db, usersCollection, uid), { profilePicture: downloadURL });
  
      return { success: true, message: "Profile picture updated successfully", url: downloadURL };
    } catch (error) {
      console.error("Error uploading profile picture:", error);
      return { success: false, message: error.message };
    }
  };



  
/**
 * Deletes a user's profile picture from Storage and Firestore
 * @param {string} uid - User ID
 */
export const deleteProfilePicture = async (uid) => {
  try {
    if (!uid) throw new Error("Invalid user ID.");

    // Reference to the user's profile picture
    const imageRef = ref(storage, `${profilePicturespath}/${uid}/profile.png`);

    // Delete image from Firebase Storage
    await deleteObject(imageRef);

    // Remove profilePicture field from Firestore
    await updateDoc(doc(db, usersCollection, uid), { profilePicture: null });

    return { success: true, message: "Profile picture deleted successfully" };
  } catch (error) {
    console.error("Error deleting profile picture:", error);
    return { success: false, message: error.message };
  }
};


/**
 * Constants for timetable storage
 */
const TIMETABLE_FILES_PATH = "timetableFiles";
const TIMETABLE_METADATA_COLLECTION = "timetableMetadata";
const GLOBAL_TIMETABLES_COLLECTION = "globalTimetables"; // Collection for admin-managed timetables

/**
 * Uploads a timetable Excel file to Firebase Storage (Admin only)
 * @param {string} uid - Admin User ID
 * @param {File} file - The Excel file to upload
 * @param {string} timetableType - Type of timetable (semesterTests, exams, etc.)
 * @param {Array} processedData - The processed timetable data (JSON)
 * @param {boolean} isGlobal - Whether this is a global timetable for all users
 * @returns {Promise<Object>} - Result of the upload operation
 */
export const uploadTimetableFile = async (uid, file, timetableType, processedData, isGlobal = false) => {
  try {
    if (!uid || !file || !timetableType) {
      throw new Error("Missing required parameters for timetable upload");
    }

    // Create a storage reference - using the correct path structure that matches our rules
    const fileName = `${timetableType}_${Date.now()}.xlsx`;
    const fileRef = ref(storage, `${TIMETABLE_FILES_PATH}/${isGlobal ? 'global' : uid}/${fileName}`);

    // Upload the file
    await uploadBytes(fileRef, file);
    const downloadURL = await getDownloadURL(fileRef);

    // Store metadata and processed data in Firestore
    const timetableMetadata = {
      fileName: file.name,
      originalName: file.name,
      fileSize: file.size,
      fileType: file.type,
      uploadDate: new Date().toISOString(),
      downloadURL: downloadURL,
      timetableType: timetableType,
      processedData: processedData,
      isGlobal: isGlobal,
      uploadedBy: uid
    };

    if (isGlobal) {
      // For global timetables, store in the globalTimetables collection
      const globalRef = doc(db, GLOBAL_TIMETABLES_COLLECTION, timetableType);
      await setDoc(globalRef, timetableMetadata);
      
      return {
        success: true,
        message: "Global timetable uploaded successfully",
        metadata: timetableMetadata,
        data: processedData
      };
    } else {
      // For personal timetables, store in user's timetable metadata document
      const metadataRef = doc(db, TIMETABLE_METADATA_COLLECTION, uid);
      const metadataSnap = await getDoc(metadataRef);

      if (metadataSnap.exists()) {
        // Update existing document
        const existingData = metadataSnap.data();
        const updatedTimetables = {
          ...existingData.timetables,
          [timetableType]: timetableMetadata
        };
        
        await updateDoc(metadataRef, { 
          timetables: updatedTimetables,
          lastUpdated: new Date().toISOString()
        });
      } else {
        // Create new document
        await setDoc(metadataRef, {
          userId: uid,
          timetables: {
            [timetableType]: timetableMetadata
          },
          lastUpdated: new Date().toISOString()
        });
      }

      return {
        success: true,
        message: "Personal timetable uploaded successfully",
        metadata: timetableMetadata,
        data: processedData
      };
    }
  } catch (error) {
    console.error("Error uploading timetable:", error);
    return { success: false, message: error.message };
  }
};

/**
 * Retrieves all timetable data for a user, including global timetables
 * @param {string} uid - User ID
 * @param {Object} userProfile - User profile including role information
 * @returns {Promise<Object>} - All timetable data for the user
 */
export const getUserTimetables = async (uid, userProfile = null) => {
  try {
    if (!uid) throw new Error("Invalid user ID");
    
    let result = {
      success: true,
      timetables: {},
      globalTimetablesAvailable: false
    };

    // First, check for global timetables that apply to all users
    try {
      // Get all documents from globalTimetables collection
      const globalTimetablesSnap = await getDocs(collection(db, GLOBAL_TIMETABLES_COLLECTION));
      
      if (!globalTimetablesSnap.empty) {
        result.globalTimetablesAvailable = true;
        
        // Add global timetables to the result
        globalTimetablesSnap.forEach((doc) => {
          const timetableData = doc.data();
          const timetableType = doc.id; // The document ID is the timetable type
          
          result.timetables[timetableType] = timetableData;
        });
      }
    } catch (globalError) {
      console.warn("Error retrieving global timetables:", globalError);
      // Continue to personal timetables even if global retrieval fails
    }

    // Then, get any personal timetables for the user
    const metadataRef = doc(db, TIMETABLE_METADATA_COLLECTION, uid);
    const metadataSnap = await getDoc(metadataRef);

    if (metadataSnap.exists()) {
      const data = metadataSnap.data();
      
      // Merge personal timetables with global ones (personal ones take precedence)
      if (data.timetables) {
        result.timetables = { ...result.timetables, ...data.timetables };
      }
      
      result.lastUpdated = data.lastUpdated;
    }

    // Add admin flag to the result if applicable
    if (userProfile && userProfile.role === 'admin') {
      result.isAdmin = true;
    }

    return result;
  } catch (error) {
    console.error("Error retrieving timetables:", error);
    return { success: false, message: error.message, timetables: {} };
  }
};

/**
 * Deletes a timetable file and its metadata
 * @param {string} uid - User ID
 * @param {string} timetableType - Type of timetable to delete
 * @param {boolean} isGlobal - Whether this is a global timetable
 * @returns {Promise<Object>} - Result of the delete operation
 */
export const deleteTimetable = async (uid, timetableType, isGlobal = false) => {
  try {
    if (!uid || !timetableType) {
      throw new Error("Missing required parameters for timetable deletion");
    }

    // Reference depends on whether we're deleting a global or personal timetable
    let metadataRef;
    let data;
    
    if (isGlobal) {
      metadataRef = doc(db, GLOBAL_TIMETABLES_COLLECTION, timetableType);
      const metadataSnap = await getDoc(metadataRef);
      
      if (!metadataSnap.exists()) {
        return { success: false, message: "No global timetable found" };
      }
      
      data = metadataSnap.data();
    } else {
      metadataRef = doc(db, TIMETABLE_METADATA_COLLECTION, uid);
      const metadataSnap = await getDoc(metadataRef);
      
      if (!metadataSnap.exists()) {
        return { success: false, message: "No timetable metadata found" };
      }
      
      data = metadataSnap.data();
      
      if (!data.timetables || !data.timetables[timetableType]) {
        return { success: false, message: `No ${timetableType} timetable found` };
      }
    }

    // Get the file URL to delete from storage
    const timetableData = isGlobal ? data : data.timetables[timetableType];
    
    if (timetableData.downloadURL) {
      // Create a reference from the download URL
      const fileRef = ref(storage, timetableData.downloadURL);
      
      try {
        // Delete the file from storage
        await deleteObject(fileRef);
      } catch (storageError) {
        console.warn("Could not delete file from storage:", storageError);
        // Continue with metadata deletion even if file deletion fails
      }
    }

    if (isGlobal) {
      // Delete the global timetable document
      await deleteDoc(metadataRef);
    } else {
      // Remove the timetable from personal metadata
      const updatedTimetables = { ...data.timetables };
      delete updatedTimetables[timetableType];

      // Update Firestore
      await updateDoc(metadataRef, { 
        timetables: updatedTimetables,
        lastUpdated: new Date().toISOString()
      });
    }

    return { 
      success: true, 
      message: `${isGlobal ? 'Global' : 'Personal'} ${timetableType} timetable deleted successfully` 
    };
  } catch (error) {
    console.error("Error deleting timetable:", error);
    return { success: false, message: error.message };
  }
};

/**
 * Upload an image to Firebase Storage
 * @param {File} file - The file to upload
 * @param {string} path - Storage path (e.g., 'posts/userId/filename.jpg')
 * @param {boolean} compress - Whether to compress the image (default: false)
 * @returns {Promise<{success: boolean, url?: string, message?: string}>}
 */
export const uploadImage = async (file, path, compress = false) => {
  try {
    let fileToUpload = file;
    
    // Optional: Compress image if requested using Compressor.js
    if (compress && file.type.match('image.*')) {
      fileToUpload = await compressImage(file);
    }
    
    const storageRef = ref(storage, path);
    const snapshot = await uploadBytes(storageRef, fileToUpload);
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    return {
      success: true,
      url: downloadURL
    };
  } catch (error) {
    console.error('Error uploading image:', error);
    return {
      success: false,
      message: error.message
    };
  }
};

/**
 * Delete an image from Firebase Storage
 * @param {string} url - The download URL or full path of the image
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const deleteImage = async (url) => {
  try {
    const storageRef = ref(storage, url);
    await deleteObject(storageRef);
    
    return {
      success: true
    };
  } catch (error) {
    console.error('Error deleting image:', error);
    return {
      success: false,
      message: error.message
    };
  }
};

/**
 * Compress an image file using Compressor.js (same as profile picture upload)
 * @param {File} file - The image file to compress
 * @param {number} maxWidth - Maximum width (default: 1200)
 * @param {number} maxHeight - Maximum height (default: 1200)
 * @param {number} quality - Image quality 0-1 (default: 0.8)
 * @returns {Promise<File>}
 */
const compressImage = async (file, maxWidth = 1200, maxHeight = 1200, quality = 0.8) => {
  return new Promise((resolve, reject) => {
    new Compressor(file, {
      maxWidth,
      maxHeight,
      quality,
      success: resolve,
      error: reject,
    });
  });
};
